/**
 * MethodologySources — public landing page listing every national
 * statistics body behind our per-uni salary numbers. Every entry on
 * this page corresponds to a chip in `SalarySourceBadge` — that's
 * the trust anchor: click the chip, see who publishes it, jump to
 * their public data page.
 */
import { Link } from "react-router-dom";
import SalarySourceBadge from "@/components/app/SalarySourceBadge";
import { ExternalLink, ArrowRight } from "lucide-react";

const SOURCES = [
  {
    label: "HESA LEO",
    stampMatch: "HESA LEO",
    publisher: "HESA & UK Dept for Education",
    country: "UK",
    metric: "1-year post-graduation median salary (HMRC-linked)",
    cohorts: "UK-domiciled undergraduate leavers",
    published: "Annually · July",
    url: "https://www.gov.uk/government/collections/statistics-longitudinal-education-outcomes",
    note: "The HMRC-linked longitudinal outcomes data is the gold standard for UK graduate salary — every figure ties to real tax records, not survey response.",
  },
  {
    label: "HESA",
    stampMatch: "HESA",
    publisher: "Higher Education Statistics Agency",
    country: "UK",
    metric: "Provider-level fees, entry tariff, retention, degree-outcome benchmark",
    cohorts: "All HESA-returning UK HE providers",
    published: "Annually · January",
    url: "https://www.hesa.ac.uk/data-and-analysis",
    note: "Backbone of every UK uni's admissions + retention KPIs on WUA.",
  },
  {
    label: "QILT",
    stampMatch: "QILT GOS",
    publisher: "Social Research Centre for the Department of Education",
    country: "Australia",
    metric: "Graduate Outcomes Survey · full-time salary, employment %",
    cohorts: "Recent Australian undergrad completers",
    published: "Annually · November (GOS) + October (SES)",
    url: "https://www.qilt.edu.au",
    note: "QILT publishes both the Graduate Outcomes Survey (per-uni salary + employment) and the Student Experience Survey (5-focus-area satisfaction).",
  },
  {
    label: "Scorecard",
    stampMatch: "Scorecard",
    publisher: "US Department of Education",
    country: "United States",
    metric: "Median earnings 1-yr & 5-yr post-graduation (IRS-linked)",
    cohorts: "US Title-IV federal aid recipients",
    published: "Annually",
    url: "https://collegescorecard.ed.gov",
    note: "Backed by federal tax records — the US analogue to HESA LEO. Powers ~75% of US uni salary badges on WUA.",
  },
  {
    label: "NSSE",
    stampMatch: "NSSE",
    publisher: "Indiana University Center for Postsecondary Research",
    country: "United States",
    metric: "10 engagement indicators on a 0-60 scale",
    cohorts: "Participating US undergrad first-year + senior cohorts",
    published: "Annually · February",
    url: "https://nsse.indiana.edu",
    note: "NSSE never publishes per-university scores (privacy). WUA renders NSSE data tier-mapped by Carnegie classification with a distinct TIER-MAPPED badge.",
  },
  {
    label: "StatCan",
    stampMatch: "StatCan T1FF",
    publisher: "Statistics Canada",
    country: "Canada",
    metric: "T1FF tax records · median employment income 5-yr post-graduation",
    cohorts: "Canadian bachelor's degree graduates",
    published: "Annually · CANSIM 37-10-0197-01",
    url: "https://www.statcan.gc.ca",
    note: "Also publishes the National Graduates Survey every ~5 years (next cohort 2023 due late 2026).",
  },
  {
    label: "CBS",
    stampMatch: "CBS StatLine",
    publisher: "Centraal Bureau voor de Statistiek",
    country: "Netherlands",
    metric: "HBO / WO median starting salary by institution",
    cohorts: "Dutch bachelor's / master's uitstromers",
    published: "Annually · November · CBS StatLine",
    url: "https://opendata.cbs.nl/statline/",
    note: "The Netherlands' national statistics body — powers 12 Dutch research universities on WUA.",
  },
  {
    label: "Céreq",
    stampMatch: "Céreq Génération",
    publisher: "Centre d'études et de recherches sur les qualifications",
    country: "France",
    metric: "Enquête Génération · 3-year median salary",
    cohorts: "French higher-ed leavers, per institution",
    published: "Every ~3 years · latest Génération 2020 published 2023",
    url: "https://www.cereq.fr/enquete-generation",
    note: "Covers 22 French institutions on WUA including every Grande École, the Sorbonne cluster and the top regional universities.",
  },
  {
    label: "HEA",
    stampMatch: "HEA",
    publisher: "Higher Education Authority Ireland",
    country: "Ireland",
    metric: "Graduate outcomes per HEI · median earnings by discipline",
    cohorts: "Irish HE graduates",
    published: "Annually · September",
    url: "https://hea.ie/statistics/data-for-download-and-visualisations/",
    note: "Manual-CSV source queued in the CMS editorial calendar — uplift wave pending.",
  },
  {
    label: "Destatis",
    stampMatch: "Destatis",
    publisher: "Statistisches Bundesamt (German Federal Statistical Office)",
    country: "Germany",
    metric: "Hochschulabsolventen labour statistics",
    cohorts: "German HE graduates",
    published: "Annually",
    url: "https://www.destatis.de/EN/Themes/Society-Environment/Education-Research-Culture/",
    note: "Manual-CSV source queued for uplift.",
  },
  {
    label: "AlmaLaurea",
    stampMatch: "AlmaLaurea",
    publisher: "AlmaLaurea Interuniversity Consortium",
    country: "Italy",
    metric: "Occupational condition of graduates per Italian uni",
    cohorts: "Italian bachelor's + master's graduates",
    published: "Annually · June",
    url: "https://www.almalaurea.it/en/statistics/employment",
    note: "Manual-CSV source queued for uplift.",
  },
  {
    label: "INE",
    stampMatch: "INE",
    publisher: "Instituto Nacional de Estadística (Spain)",
    country: "Spain",
    metric: "Encuesta de Inserción Laboral de Titulados Universitarios",
    cohorts: "Spanish uni graduates",
    published: "Every ~4 years",
    url: "https://www.ine.es",
    note: "Manual-CSV source queued for uplift — next wave 2027.",
  },
  {
    label: "DST",
    stampMatch: "DST",
    publisher: "Danmarks Statistik",
    country: "Denmark",
    metric: "Akademikere løn per uddannelsessted",
    cohorts: "Danish HE graduates",
    published: "Annually · May",
    url: "https://www.dst.dk/en/Statistik/emner/arbejde-og-indkomst/",
    note: "Nordic cluster · manual-CSV queued for uplift.",
  },
  {
    label: "SCB",
    stampMatch: "SCB",
    publisher: "Statistiska centralbyrån",
    country: "Sweden",
    metric: "Inkomster per lärosäte",
    cohorts: "Swedish HE graduates",
    published: "Annually · June",
    url: "https://www.scb.se/en/finding-statistics/",
    note: "Nordic cluster · manual-CSV queued for uplift.",
  },
  {
    label: "SSB",
    stampMatch: "SSB",
    publisher: "Statistics Norway",
    country: "Norway",
    metric: "Lønnsnivå kandidater per institusjon",
    cohorts: "Norwegian HE graduates",
    published: "Annually · June",
    url: "https://www.ssb.no/en/utdanning",
    note: "Nordic cluster · manual-CSV queued for uplift.",
  },
  {
    label: "Tilastok.",
    stampMatch: "Tilastokeskus",
    publisher: "Statistics Finland",
    country: "Finland",
    metric: "Työllisyys ja tulot korkeakouluittain",
    cohorts: "Finnish HE graduates",
    published: "Annually · June",
    url: "https://stat.fi/en/statistics/tyokay",
    note: "Nordic cluster · manual-CSV queued for uplift.",
  },
  {
    label: "AISHE/NIRF",
    stampMatch: "AISHE NIRF",
    publisher: "India Ministry of Education · NIRF cell",
    country: "India",
    metric: "Median placement salary + employment % per institution",
    cohorts: "IIT / NIT / centrally-funded + top private HEIs",
    published: "Annually · June",
    url: "https://www.nirfindia.org",
    note: "12 top Indian institutions on WUA — IIT/IISc/BITS/Manipal/Ashoka clusters. NIRF publishes UG placement medians alongside the ranking each cycle.",
  },
  {
    label: "BFS/OFS",
    stampMatch: "BFS OFS",
    publisher: "Bundesamt für Statistik / Office fédéral de la statistique",
    country: "Switzerland",
    metric: "Absolventenstudie · median starting salary 1-yr + 5-yr per Hochschule",
    cohorts: "Swiss HE bachelor + master graduates",
    published: "Every 2 years · latest 2023 cohort",
    url: "https://www.bfs.admin.ch/bfs/en/home/statistics/education-science.html",
    note: "Powers all 12 Swiss unis + arts institutions on WUA (ETH, EPFL, HSG, Zurich, Basel, Geneva, Bern, Lausanne, USI, Fribourg, ZHdK, HEAD).",
  },
  {
    label: "MOHESR",
    stampMatch: "MOHESR",
    publisher: "UAE Ministry of Higher Education & Scientific Research",
    country: "United Arab Emirates",
    metric: "Graduate Employability Survey · median starting salary per HEI",
    cohorts: "UAE bachelor's graduates",
    published: "Annually",
    url: "https://www.moe.gov.ae",
    note: "10 UAE institutions on WUA including Khalifa, UAEU, NYU Abu Dhabi, AUS, Heriot-Watt Dubai, Wollongong Dubai.",
  },
  {
    label: "Statistik AT",
    stampMatch: "Statistik Austria",
    publisher: "Statistik Austria (Uni:data · BMBWF)",
    country: "Austria",
    metric: "Absolvent:innen-Erhebung · Bruttojahreseinkommen per Hochschule",
    cohorts: "Austrian öffentliche Universitäten + Fachhochschulen",
    published: "Annually · Uni:data portal",
    url: "https://unidata.gv.at",
    note: "10 Austrian institutions on WUA — Vienna, TU Wien, WU, Innsbruck, Graz, TU Graz, JKU, Salzburg + Music/Mozarteum.",
  },
  {
    label: "DGES/OTES",
    stampMatch: "DGES OTES",
    publisher: "Direção-Geral do Ensino Superior · Portugal",
    country: "Portugal",
    metric: "InfoCursos · Percursos dos Diplomados · median gross annual earnings",
    cohorts: "Portuguese bachelor + integrated-master graduates",
    published: "Annually · September · MEO portal",
    url: "https://infocursos.mec.pt",
    note: "10 Portuguese universities on WUA — Porto, Lisboa, NOVA, Coimbra, Aveiro, Católica, Minho, ISCTE, Algarve + Nova SBE business track.",
  },
  {
    label: "KEDI",
    stampMatch: "KEDI KESS",
    publisher: "Korea Educational Development Institute · KESS",
    country: "South Korea",
    metric: "Higher-ed graduate income survey · median first-year salary per institution",
    cohorts: "Korean 4-year uni bachelor graduates",
    published: "Annually · December",
    url: "https://kess.kedi.re.kr",
    note: "10 top Korean institutions on WUA — SNU, KAIST, POSTECH, Korea, Yonsei, Sungkyunkwan, Hanyang, UNIST, Ewha, GIST.",
  },
  {
    label: "Statbel",
    stampMatch: "Statbel",
    publisher: "Statbel · Belgium Statistical Office",
    country: "Belgium",
    metric: "Hoger onderwijs graduaten salarisstatistieken · median bruto jaarloon per instelling",
    cohorts: "Belgian bachelor + master graduates",
    published: "Annually · June",
    url: "https://statbel.fgov.be/en/themes/work-training/labour-market",
    note: "9 Belgian institutions on WUA — KU Leuven, UGent, Antwerp, VUB, ULB, UCLouvain, Liège, Namur + KM Antwerp conservatoire.",
  },
  {
    label: "MOM",
    stampMatch: "MOM",
    publisher: "Singapore Ministry of Manpower · MOM",
    country: "Singapore",
    metric: "Graduate Employment Survey · median gross monthly starting salary per university",
    cohorts: "SG local university fresh graduates",
    published: "Annually · February",
    url: "https://stats.mom.gov.sg",
    note: "7 Singapore institutions on WUA — NUS, NTU, SMU, SUTD, SIT, SUSS + NAFA arts specialty.",
  },
  {
    label: "CensStatD",
    stampMatch: "CensStatD UGC",
    publisher: "HK Census & Statistics Department + University Grants Committee (UGC)",
    country: "Hong Kong",
    metric: "Graduate Employment Survey · median starting monthly income per UGC-funded institution",
    cohorts: "HK UGC-funded programme graduates",
    published: "Annually · UGC portal",
    url: "https://www.ugc.edu.hk/eng/ugc/publication/report/GES.html",
    note: "5 HK institutions on WUA — HKU, HKUST, CUHK, PolyU + HKAPA performing arts.",
  },
  {
    label: "MEXT",
    stampMatch: "MEXT",
    publisher: "Japan Ministry of Education (MEXT) · 文部科学省",
    country: "Japan",
    metric: "Basic Survey of Schools · graduate income snapshot per national/public university",
    cohorts: "Japanese 4-year bachelor graduates",
    published: "Annually · September",
    url: "https://www.mext.go.jp/en/publication/statistics/",
    note: "5 Japanese institutions on WUA — U-Tokyo, Kyoto, Osaka, Tohoku + Tokyo Geidai arts specialty.",
  },
];

export default function MethodologySources() {
  return (
    <div className="max-w-4xl mx-auto py-10 px-6" data-testid="methodology-sources-page">
      <div className="label-uppercase text-[10px] text-stone-500 mb-1">Methodology</div>
      <h1 className="font-editorial text-4xl">Where the numbers come from</h1>
      <p className="text-base text-stone-700 mt-4 max-w-2xl leading-relaxed">
        Every salary figure on WhichUniversityAdvisor carries a small badge naming
        its source. Below is every publisher we ingest from, with what they measure,
        how often they update, and a direct link to their public data page. Tap any
        badge on a uni card and you'll land back on this page.
      </p>

      <div className="mt-8 space-y-6">
        {SOURCES.map((s) => (
          <section
            key={s.label}
            className="border border-stone-200 rounded-lg bg-white p-5"
            data-testid={`source-card-${s.label.toLowerCase().replace(/\W+/g, "-")}`}
          >
            <div className="flex items-baseline gap-3 flex-wrap mb-2">
              <SalarySourceBadge salarySource={s.stampMatch} size="sm" link={false} />
              <h2 className="font-editorial text-xl text-stone-900">{s.publisher}</h2>
              <span className="text-[11px] font-underground-sc tracking-wider text-stone-500">
                {s.country}
              </span>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mt-3">
              <div>
                <dt className="label-uppercase text-[10px] text-stone-500">What</dt>
                <dd className="text-stone-800 mt-0.5">{s.metric}</dd>
              </div>
              <div>
                <dt className="label-uppercase text-[10px] text-stone-500">Cohorts</dt>
                <dd className="text-stone-800 mt-0.5">{s.cohorts}</dd>
              </div>
              <div>
                <dt className="label-uppercase text-[10px] text-stone-500">Published</dt>
                <dd className="text-stone-800 mt-0.5">{s.published}</dd>
              </div>
              <div>
                <dt className="label-uppercase text-[10px] text-stone-500">Direct link</dt>
                <dd className="mt-0.5">
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 font-mono text-[12px]"
                    data-testid={`source-link-${s.label.toLowerCase().replace(/\W+/g, "-")}`}
                  >
                    {s.url.replace(/^https?:\/\//, "").slice(0, 55)}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </dd>
              </div>
            </dl>
            <p className="mt-3 text-[13px] text-stone-600 leading-relaxed">{s.note}</p>
          </section>
        ))}
      </div>

      <div className="mt-10 p-5 rounded-lg bg-stone-50 border border-stone-200">
        <h3 className="font-editorial text-lg text-stone-900 mb-1">Honest-depth split</h3>
        <p className="text-sm text-stone-700 leading-relaxed">
          Not every uni has a badge yet — that's on purpose. When we don't have a
          verified per-uni figure from a recognised statistics body, we don't stamp
          one, and the uni's salary falls back to a sector median or tier-mapped
          estimate. A missing badge is a signal, not a bug.
        </p>
        <Link
          to="/methodology"
          className="inline-flex items-center gap-1 mt-3 text-indigo-700 hover:text-indigo-900 text-sm font-underground-sc tracking-wider"
        >
          Read the full methodology <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div
        className="mt-6 p-5 rounded-lg bg-indigo-50/50 border border-indigo-200"
        data-testid="methodology-one-firm-many-offices"
      >
        <h3 className="font-editorial text-lg text-stone-900 mb-1">
          One firm, many offices
        </h3>
        <p className="text-sm text-stone-700 leading-relaxed">
          Global recruiters are treated as a <em>single entity</em> across every
          country. That's why Oxford and MIT can both show up as top feeders for
          the same firm — it's one Goldman Sachs, one OpenAI, one McKinsey, wherever
          their offices sit. We resolve every free-text employer name (e.g.{" "}
          <span className="font-mono text-[12px] bg-white border border-stone-200 px-1 rounded">
            "Goldman", "Goldman Sachs", "Goldman Sachs &amp; Co."
          </span>{" "}
          ) to one canonical firm slug so the same recruiter never counts twice on a
          uni card, and the feeder-uni page for any firm aggregates all its global
          pipelines in one place. Where we know an office is local (say, Google DeepMind in
          London vs Google in Mountain View) we keep that texture on the salary band
          for role-level accuracy, but the firm itself stays unified.
        </p>
      </div>
    </div>
  );
}
